import React, { useState, useRef, useEffect, useCallback } from 'react';
import DatePicker from '../components/DatePicker';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAppStore } from '../store/useAppStore';
import { qk } from '../services/queryKeys';
import { fetchAttendance, fetchEmployees, clockPunchApi, regularizeApi, fetchTeamAttendance, fetchMyEmployee, fetchHolidays, fetchShifts } from '../services/api';
import Avatar from '../components/Avatar';
import Chip from '../components/Chip';
import Button from '../components/Button';
import Icon from '../components/Icon';
import Modal from '../components/Modal';
import Alert from '../components/Alert';
import { AttendanceWeekTable, AttendanceWeekTimeline } from '../components/AttendanceWeek';
import { buildWeekSummaries, getWeekKeys, shiftKey, todayKey, fmtShiftTime } from '../lib/attendanceWeek';
import type { ClockQueueItem } from '../types';

// ── Drum Time Picker ──────────────────────────────────────────────────────────
const HOURS = Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, '0'));
const MINUTES = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, '0'));
const PERIODS = ['AM', 'PM'];
const ITEM_H = 44;
const VISIBLE = 5; // rows shown at once

function DrumColumn({
  items,
  selected,
  onSelect,
}: {
  items: string[];
  selected: string;
  onSelect: (v: string) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const selIdx = items.indexOf(selected);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Scroll to selected whenever it changes (including external "Now" updates)
  useEffect(() => {
    ref.current?.scrollTo({ top: selIdx * ITEM_H, behavior: 'smooth' });
  }, [selIdx]);

  // Debounce: update state only after user stops scrolling (avoids fighting the scroll animation)
  const handleScroll = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      if (!ref.current) return;
      const idx = Math.round(ref.current.scrollTop / ITEM_H);
      const clamped = Math.max(0, Math.min(items.length - 1, idx));
      if (items[clamped] !== selected) onSelect(items[clamped]);
    }, 120);
  }, [items, selected, onSelect]);

  const spacer = Math.floor(VISIBLE / 2); // 2 rows of padding top & bottom

  return (
    <div
      ref={ref}
      onScroll={handleScroll}
      style={{
        flex: 1,
        height: ITEM_H * VISIBLE,
        overflowY: 'scroll',
        scrollSnapType: 'y mandatory',
        scrollbarWidth: 'none',
        // @ts-ignore
        msOverflowStyle: 'none',
      }}
    >
      <div style={{ height: ITEM_H * spacer }} />
      {items.map((v, i) => (
        <div
          key={v}
          onClick={() => ref.current?.scrollTo({ top: i * ITEM_H, behavior: 'smooth' })}
          style={{
            height: ITEM_H,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            scrollSnapAlign: 'center',
            cursor: 'pointer',
            fontSize: 16,
            fontWeight: v === selected ? 700 : 400,
            color: v === selected ? 'var(--accent, #3b82f6)' : '#9ca3af',
            borderRadius: 8,
            transition: 'color 0.15s',
            userSelect: 'none',
          }}
        >
          {v}
        </div>
      ))}
      <div style={{ height: ITEM_H * spacer }} />
    </div>
  );
}

function TimePicker({
  value,
  onChange,
}: {
  value: string; // HH:MM 24h
  onChange: (v: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  const parseValue = (v: string) => {
    if (!v) {
      const now = new Date();
      const h = now.getHours();
      return {
        hour: String(h % 12 || 12).padStart(2, '0'),
        minute: String(now.getMinutes()).padStart(2, '0'),
        period: h >= 12 ? 'PM' : 'AM',
      };
    }
    const [hStr, mStr] = v.split(':');
    const h24 = parseInt(hStr, 10);
    return {
      hour: String(h24 % 12 || 12).padStart(2, '0'),
      minute: mStr,
      period: h24 >= 12 ? 'PM' : 'AM',
    };
  };

  const { hour, minute, period } = parseValue(value);

  const to24 = (h: string, m: string, p: string) => {
    let h24 = parseInt(h, 10);
    if (p === 'AM' && h24 === 12) h24 = 0;
    if (p === 'PM' && h24 !== 12) h24 += 12;
    return `${String(h24).padStart(2, '0')}:${m}`;
  };

  const setNow = () => {
    const now = new Date();
    onChange(`${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`);
  };

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const display = value ? `${hour} : ${minute} ${period}` : '';

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      {/* Trigger */}
      <div
        className="t-input"
        onClick={() => setOpen((o) => !o)}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer',
          userSelect: 'none',
          color: display ? 'var(--text)' : 'var(--text-muted)',
          outline: open ? '2px solid var(--accent, #3b82f6)' : undefined,
        }}
      >
        <span>{display || 'Select time'}</span>
        <Icon name="clock" size={16} color="var(--text-muted, #9ca3af)" />
      </div>

      {/* Dropdown — opens upward, full-width, solid opaque background */}
      {open && (
        <div
          style={{
            position: 'absolute',
            zIndex: 500,
            bottom: 'calc(100% + 6px)',
            left: 0,
            right: 0,       // stretch to full input width
            backgroundColor: '#ffffff',
            border: '1px solid #e5e7eb',
            borderRadius: 14,
            boxShadow: '0 12px 40px rgba(0,0,0,0.18)',
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '12px 16px 10px',
              borderBottom: '1px solid #f3f4f6',
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 500, fontSize: 14, color: '#374151' }}>
              <Icon name="clock" size={14} color="#6b7280" /> Time
            </span>
            <span style={{ fontWeight: 700, fontSize: 17, letterSpacing: 1, color: '#1f2937' }}>
              {hour} : {minute}{' '}
              <span style={{ color: 'var(--accent, #3b82f6)' }}>{period}</span>
            </span>
          </div>

          {/* Center-selection highlight rail */}
          <div style={{ position: 'relative', padding: '0 12px' }}>
            {/* The rail sits behind the columns at the vertical midpoint */}
            <div
              style={{
                position: 'absolute',
                inset: `${Math.floor(VISIBLE / 2) * ITEM_H}px 12px`,
                height: ITEM_H,
                background: '#eff6ff',
                borderRadius: 10,
                pointerEvents: 'none',
              }}
            />
            <div style={{ display: 'flex', gap: 4, position: 'relative' }}>
              <DrumColumn items={HOURS} selected={hour} onSelect={(h) => onChange(to24(h, minute, period))} />
              <DrumColumn items={MINUTES} selected={minute} onSelect={(m) => onChange(to24(hour, m, period))} />
              <DrumColumn items={PERIODS} selected={period} onSelect={(p) => onChange(to24(hour, minute, p))} />
            </div>
          </div>

          {/* Footer */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              padding: '10px 16px',
              borderTop: '1px solid #f3f4f6',
            }}
          >
            <button
              type="button"
              onClick={setNow}
              style={{ background: 'none', border: 'none', color: 'var(--accent, #3b82f6)', fontWeight: 600, cursor: 'pointer', fontSize: 14 }}
            >
              Now
            </button>
            <button
              type="button"
              onClick={() => setOpen(false)}
              style={{ background: 'none', border: 'none', color: 'var(--accent, #3b82f6)', fontWeight: 600, cursor: 'pointer', fontSize: 14 }}
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Attendance() {
  const { role, profileId, employeeId, modal, openModal, closeModal, toast } = useAppStore();
  const myId = employeeId || profileId;
  const queryClient = useQueryClient();

  const today = new Date().toISOString().slice(0, 10);

  // Local clock queue state
  const [clockQueue, setClockQueue] = useState<ClockQueueItem[]>([]);
  const [regForm, setRegForm] = useState({ date: '', time: '', type: 'clock_in', reason: 'missed_punch' });
  const [ledgerPeriod, setLedgerPeriod] = useState('all');
  const [ledgerEventType, setLedgerEventType] = useState('all');

  const { data: attendanceData = [] } = useQuery({
    queryKey: qk.attendance(myId),
    queryFn: () => fetchAttendance(myId),
    enabled: !!employeeId,
  });

  const isHrOrMss = role === 'mss' || role === 'hr';

  const { data: employees = [] } = useQuery({
    queryKey: qk.employees(),
    queryFn: fetchEmployees,
    enabled: isHrOrMss,
  });

  const { data: teamData = [] } = useQuery({
    queryKey: ['teamAttendance', today],
    queryFn: () => fetchTeamAttendance(today),
    enabled: isHrOrMss,
    refetchInterval: 30_000,
  });

  const { data: me } = useQuery({
    queryKey: ['employees', 'me'],
    queryFn: fetchMyEmployee,
    enabled: !employeeId,
  });

  const { data: holidays = [] } = useQuery({ queryKey: qk.holidays(), queryFn: fetchHolidays });
  const { data: shifts = [] } = useQuery({ queryKey: qk.shifts(), queryFn: fetchShifts });

  const [weekAnchor, setWeekAnchor] = useState(todayKey());
  const weekKeys = getWeekKeys(weekAnchor);
  const holidayNameByDate = new Map(holidays.map((h) => [h.date, h.name]));
  const daySummaries = buildWeekSummaries({
    weekKeys,
    entries: attendanceData.map((a) => ({ punchType: a.punchType, punchedAt: a.punchedAt })),
    weeklyDayOff: me?.weeklyDayOff,
    holidayNameByDate,
    nowMs: Date.now(),
  });
  const myShift = shifts.find((s) => s.name === me?.shift);
  const shiftLabel = myShift
    ? `${myShift.name} · ${fmtShiftTime(myShift.startTime)} - ${fmtShiftTime(myShift.endTime)}`
    : me?.shift || 'No shift assigned';
  const weekRangeLabel = `${weekKeys[0].split('-').reverse().join('-')} - ${weekKeys[6].split('-').reverse().join('-')}`;

  // Compute punch state from attendance data
  const attendance = attendanceData;
  const todayPunches = attendance.filter((e) => e.punchedAt.startsWith(today));
  const lastPunch = [...attendance]
    .sort((a, b) => b.punchedAt.localeCompare(a.punchedAt))[0]?.punchType ?? null;
  const nextPunch: 'clock_in' | 'clock_out' = lastPunch === 'clock_in' ? 'clock_out' : 'clock_in';

  const isQueued = clockQueue.some((q) => q.status === 'queued');

  const myLedger = [...attendance].sort((a, b) => b.punchedAt.localeCompare(a.punchedAt));

  const teamAttendance = employees.filter((e) => e.status === 'active').map((emp) => {
    const entry = teamData.find((t) => t.employeeId === emp.id);
    return {
      emp,
      present: !!entry,
      status: entry?.lastPunchType ?? null,
      lastTime: entry ? new Date(entry.lastPunchAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '—',
    };
  });

  const punchMutation = useMutation({
    mutationFn: async (punchType: 'clock_in' | 'clock_out') => {
      if (!employeeId) throw new Error('Employee account not linked');
      const tempId = `temp_${Date.now()}`;
      const item: ClockQueueItem = {
        tempId,
        punchType,
        deviceCreatedAt: new Date().toISOString(),
        idempotencyKey: `punch_${employeeId}_${Date.now()}`,
        status: 'queued',
      };
      setClockQueue((q) => [...q, item]);
      const entry = await clockPunchApi(employeeId, punchType);
      setClockQueue((q) => q.filter((x) => x.tempId !== tempId));
      return entry;
    },
    onSuccess: (entry) => {
      queryClient.invalidateQueries({ queryKey: qk.attendance(myId) });
      toast(`${entry.punchType === 'clock_in' ? 'Clocked in' : 'Clocked out'} successfully`);
    },
    onError: () => {
      setClockQueue((q) => q.map((x) => x.status === 'queued' ? { ...x, status: 'failed' as const } : x));
      toast('Punch failed', 'warn');
    },
  });

  const regularizeMutation = useMutation({
    mutationFn: (form: { date: string; time: string; type: 'clock_in' | 'clock_out'; reason: string }) =>
      regularizeApi(employeeId, form.date, form.time, form.type, form.reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.attendance(myId) });
      toast('Regularization submitted');
      setRegForm({ date: '', time: '', type: 'clock_in', reason: '' });
      closeModal();
    },
    onError: () => toast('Regularization failed', 'danger'),
  });

  return (
    <div>
      <div className="sk-page-header">
        <h1>Attendance</h1>
      </div>

      {!employeeId && (
        <Alert variant="warn" style={{ marginBottom: 16 }}>
          Your account is not linked to an employee record. Ask your HR admin to add you to the directory with your work email, or contact support.
        </Alert>
      )}

      {/* Clock widget */}
      <div className="t-card" style={{ marginBottom: 20 }}>
        <div className="card-body" style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
          <div style={{ flex: 1 }}>
            <p style={{ fontSize: 'var(--fs-sm)', color: 'var(--ink-3)', marginBottom: 6 }}>
              {new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
            </p>
            <p style={{ fontSize: 32, fontWeight: 700, color: 'var(--ink)', fontFamily: 'var(--font-mono)' }}>
              {new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
            </p>
            <p style={{ fontSize: 'var(--fs-sm)', marginTop: 4, color: lastPunch === 'clock_in' ? 'var(--success)' : 'var(--ink-3)' }}>
              {lastPunch === 'clock_in' ? "You're clocked in" : "Not clocked in today"}
            </p>
            <p style={{ fontSize: 'var(--fs-xs)', color: 'var(--ink-3)', marginTop: 2 }}>Offline-tolerant · syncs when online</p>
            {todayPunches.length > 0 && (
              <div style={{ marginTop: 10, display: 'flex', gap: 8 }}>
                {todayPunches.map((p) => (
                  <Chip key={p.id} variant={p.punchType === 'clock_in' ? 'success' : 'neutral'}>
                    {p.punchType === 'clock_in' ? 'In' : 'Out'} {new Date(p.punchedAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                  </Chip>
                ))}
              </div>
            )}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
            <Button
              variant={nextPunch === 'clock_in' ? 'accent' : 'danger'}
              size="lg"
              style={{ minWidth: 160 }}
              disabled={!employeeId || isQueued || punchMutation.isPending}
              onClick={() => punchMutation.mutate(nextPunch)}
            >
              {isQueued || punchMutation.isPending ? (
                <>
                  <div className="t-spinner" style={{ width: 14, height: 14, borderTopColor: 'white' }} />
                  queued · will sync
                </>
              ) : (
                <>
                  <Icon name="clock" size={16} color="white" />
                  {nextPunch === 'clock_in' ? 'Clock In' : 'Clock Out'}
                </>
              )}
            </Button>
            <button
              onClick={() => openModal('regularize')}
              style={{ fontSize: 'var(--fs-sm)', color: 'var(--accent)', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'var(--font-sans)' }}
            >
              Missed a punch? Regularize →
            </button>
          </div>
        </div>
      </div>

      {/* This week */}
      {employeeId && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <button
              onClick={() => setWeekAnchor(shiftKey(weekAnchor, -7))}
              style={{ background: 'none', border: '1px solid var(--border, #e5e7eb)', borderRadius: 8, width: 28, height: 28, cursor: 'pointer' }}
            >
              <Icon name="chevron_left" size={14} />
            </button>
            <button
              onClick={() => setWeekAnchor(todayKey())}
              style={{ fontSize: 'var(--fs-sm)', color: 'var(--accent)', background: 'none', border: 'none', cursor: 'pointer' }}
            >
              This week
            </button>
            <button
              onClick={() => setWeekAnchor(shiftKey(weekAnchor, 7))}
              style={{ background: 'none', border: '1px solid var(--border, #e5e7eb)', borderRadius: 8, width: 28, height: 28, cursor: 'pointer' }}
            >
              <Icon name="chevron_right" size={14} />
            </button>
          </div>
          <AttendanceWeekTimeline days={daySummaries} shiftLabel={shiftLabel} rangeLabel={weekRangeLabel} />
          <div style={{ marginBottom: 20 }}>
            <AttendanceWeekTable days={daySummaries} shiftLabel={shiftLabel} />
          </div>
        </>
      )}

      {/* Today's punches */}
      {(todayPunches.length > 0 || clockQueue.filter(q => q.status === 'queued').length > 0) && (
        <div className="t-card" style={{ marginBottom: 20 }}>
          <div className="card-head"><h3>Today's punches</h3></div>
          <table className="t-table">
            <thead><tr><th>Type</th><th>Time</th><th>Note</th></tr></thead>
            <tbody>
              {todayPunches.map((p) => (
                <tr key={p.id}>
                  <td><Chip variant={p.punchType === 'clock_in' ? 'success' : 'neutral'}>{p.punchType.replace('_', ' ')}</Chip></td>
                  <td className="mono">{new Date(p.punchedAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}</td>
                  <td><Chip variant="neutral">{p.source}</Chip></td>
                </tr>
              ))}
              {clockQueue.filter(q => q.status === 'queued').map((q) => (
                <tr key={q.tempId}>
                  <td><Chip variant={q.punchType === 'clock_in' ? 'success' : 'neutral'}>{q.punchType.replace('_', ' ')}</Chip></td>
                  <td className="mono">{new Date(q.deviceCreatedAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}</td>
                  <td><Chip variant="warn">queued · will sync</Chip></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Team attendance table for MSS/HR */}
      {(role === 'mss' || role === 'hr') && (
        <div className="t-card" style={{ marginBottom: 20 }}>
          <div className="card-head"><h3>Team attendance — today</h3></div>
          <table className="t-table">
            <thead><tr><th>Employee</th><th>Department</th><th>Status</th><th>Last punch</th></tr></thead>
            <tbody>
              {teamAttendance.map(({ emp, present, status, lastTime }) => {
                return (
                  <tr key={emp.id}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Avatar firstName={emp.firstName} lastName={emp.lastName} color={emp.avatarColor} size="sm" />
                        <span>{emp.firstName} {emp.lastName}</span>
                      </div>
                    </td>
                    <td>{emp.department?.name ?? emp.departmentId}</td>
                    <td><Chip variant={present ? (status === 'clock_in' ? 'success' : 'neutral') : 'neutral'} dot>{present ? (status === 'clock_in' ? 'Present' : 'Left') : 'Absent'}</Chip></td>
                    <td className="mono" style={{ color: 'var(--ink-3)' }}>{lastTime}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Attendance ledger */}
      <div className="t-card">
        <div className="card-head" style={{ flexWrap: 'wrap', gap: 8 }}>
          <h3>My attendance ledger</h3>
          <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
            <select
              className="t-input"
              style={{ width: 'auto', padding: '4px 8px', fontSize: 'var(--fs-sm)' }}
              value={ledgerPeriod}
              onChange={(e) => setLedgerPeriod(e.target.value)}
            >
              <option value="all">All time</option>
              <option value="thisMonth">This month</option>
              <option value="lastMonth">Last month</option>
              <option value="thisWeek">This week</option>
            </select>
            <select
              className="t-input"
              style={{ width: 'auto', padding: '4px 8px', fontSize: 'var(--fs-sm)' }}
              value={ledgerEventType}
              onChange={(e) => setLedgerEventType(e.target.value)}
            >
              <option value="all">All events</option>
              <option value="clock_in">Clock in</option>
              <option value="clock_out">Clock out</option>
            </select>
          </div>
        </div>
        <table className="t-table">
          <thead><tr><th>Date</th><th>Type</th><th>Time</th><th>Note</th></tr></thead>
          <tbody>
            {myLedger.filter((a) => {
              const d = a.punchedAt.slice(0, 10);
              const now = new Date();
              const thisMonthStr = now.toISOString().slice(0, 7);
              const lastMonthDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
              const lastMonthStr = lastMonthDate.toISOString().slice(0, 7);
              const weekStart = new Date(now); weekStart.setDate(now.getDate() - now.getDay());
              const weekStartStr = weekStart.toISOString().slice(0, 10);
              if (ledgerPeriod === 'thisMonth' && !d.startsWith(thisMonthStr)) return false;
              if (ledgerPeriod === 'lastMonth' && !d.startsWith(lastMonthStr)) return false;
              if (ledgerPeriod === 'thisWeek' && d < weekStartStr) return false;
              if (ledgerEventType !== 'all' && a.punchType !== ledgerEventType) return false;
              return true;
            }).slice(0, 30).map((a) => (
              <tr key={a.id}>
                <td>{a.punchedAt.slice(0, 10)}</td>
                <td><Chip variant={a.punchType === 'clock_in' ? 'success' : 'neutral'}>{a.punchType.replace('_', ' ')}</Chip></td>
                <td className="mono">{new Date(a.punchedAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</td>
                <td style={{ color: 'var(--ink-3)', fontSize: 'var(--fs-sm)' }}>{a.source ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Regularize Modal */}
      {modal.type === 'regularize' && (
        <Modal
          title="Regularize attendance"
          onClose={closeModal}
          footer={
            <>
              <Button variant="ghost" onClick={() => { setRegForm({ date: '', time: '', type: 'clock_in', reason: '' }); closeModal(); }}>Cancel</Button>
              <Button
                variant="accent"
                disabled={regularizeMutation.isPending}
                onClick={() => {
                  if (!regForm.date || !regForm.time || !regForm.reason.trim()) {
                    toast('Please fill all fields', 'warn'); return;
                  }
                  regularizeMutation.mutate({ ...regForm, type: regForm.type as 'clock_in' | 'clock_out' });
                }}
              >Submit regularization</Button>
            </>
          }
        >
          <div style={{ marginBottom: 14, padding: 12, background: 'var(--warn-soft)', borderRadius: 8, fontSize: 'var(--fs-sm)', color: 'var(--warn)' }}>
            Creates a correction entry — your original record is preserved. Regularizations are reviewed by your manager.
          </div>
          <div className="form-grid">
            <div className="form-row">
              <label>Date</label>
              <DatePicker value={regForm.date} max={today} onChange={(v) => setRegForm(f => ({ ...f, date: v }))} />
            </div>
            <div className="form-row">
              <label>Punch type</label>
              <select className="t-input" value={regForm.type} onChange={(e) => setRegForm(f => ({ ...f, type: e.target.value }))}>
                <option value="clock_in">Clock In</option>
                <option value="clock_out">Clock Out</option>
              </select>
            </div>
          </div>
          <div className="form-row">
            <label>Time</label>
            <TimePicker value={regForm.time} onChange={(v) => setRegForm(f => ({ ...f, time: v }))} />
          </div>
          <div className="form-row">
            <label>Reason</label>
            <select className="t-input" value={regForm.reason} onChange={(e) => setRegForm(f => ({ ...f, reason: e.target.value }))}>
              <option value="missed_punch">Missed punch — forgot to clock in/out</option>
              <option value="device_offline">Device was offline</option>
              <option value="system_error">System error / app crash</option>
              <option value="travel_offsite">Was offsite / travelling</option>
              <option value="other">Other</option>
            </select>
          </div>
        </Modal>
      )}
    </div>
  );
}
